const React = require('react');
const { createElement: h, useEffect, useRef, useState } = React;
require('./AgentSettingsEditor.css');

var idCounter = 0;
function newId() { return 'rt-' + (++idCounter) + '-' + Date.now(); }

function AgentSettingsEditor(props) {
  var title = props.title || 'AI';
  var initialParams = props.initialParams || {};
  var position = props.position || { left: 0, top: 0, side: 'left' };
  var onClose = props.onClose;
  var onSave = props.onSave;
  var variableNodes = props.variableNodes || [];
  var popoverRef = useRef(null);
  var varDropdownRef = useRef(null);

  var [system, setSystem] = useState(
    initialParams.system !== undefined ? String(initialParams.system) : 'You are a helpful AI assistant.'
  );
  // Migrate old string variableInput to array
  var [variableInputs, setVariableInputs] = useState(function() {
    if (Array.isArray(initialParams.variableInputs) && initialParams.variableInputs.length > 0) {
      return initialParams.variableInputs;
    }
    var old = String(initialParams.variableInput || '').trim();
    return old ? [old] : [];
  });
  var [showVarDropdown, setShowVarDropdown] = useState(false);
  var [temperature, setTemperature] = useState(
    initialParams.temperature !== undefined ? Number(initialParams.temperature) : 0.7
  );
  var [maxTokens, setMaxTokens] = useState(
    initialParams.maxTokens !== undefined ? Number(initialParams.maxTokens) : 8000
  );
  var [aiTier, setAiTier] = useState(
    initialParams.aiTier === 'balance' || initialParams.aiTier === 'thorough' ? initialParams.aiTier : 'fast'
  );
  // Migrate: support both string[] and {name,description}[] formats
  var [templateKeys, setTemplateKeys] = useState(function() {
    var saved = initialParams.responseTemplate;
    if (Array.isArray(saved) && saved.length > 0) {
      return saved.map(function(k) {
        if (k && typeof k === 'object') {
          return { id: newId(), name: String(k.name || ''), description: String(k.description || '') };
        }
        return { id: newId(), name: String(k || ''), description: '' };
      });
    }
    return [];
  });

  useEffect(function() {
    function onDocumentMouseDown(event) {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(event.target)) onClose();
    }
    function onDocumentKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    return function() {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [onClose]);

  function handleSave() {
    var temp = Number(temperature);
    var tokens = Number(maxTokens);
    // Save as {name, description} objects; filter out empty names
    var keys = templateKeys
      .filter(function(k) { return k.name.trim(); })
      .map(function(k) { return { name: k.name.trim(), description: k.description.trim() }; });
    onSave({
      system: system.trim() || 'You are a helpful AI assistant.',
      variableInputs: variableInputs,
      variableInput: '',
      responseTemplate: keys,
      temperature: Number.isFinite(temp) ? Math.max(0, Math.min(1, temp)) : 0.7,
      maxTokens: Number.isFinite(tokens) ? Math.max(100, Math.min(16000, tokens)) : 8000,
      aiTier: aiTier === 'balance' || aiTier === 'thorough' ? aiTier : 'fast'
    });
  }

  function addVariableInput(vn) {
    var ref = '@' + String(vn.title || '').trim();
    if (variableInputs.indexOf(ref) < 0) {
      setVariableInputs(function(prev) { return prev.concat([ref]); });
    }
    setShowVarDropdown(false);
  }

  function removeVariableInput(ref) {
    setVariableInputs(function(prev) { return prev.filter(function(v) { return v !== ref; }); });
  }

  function addTemplateKey() {
    setTemplateKeys(function(prev) { return prev.concat([{ id: newId(), name: '', description: '' }]); });
  }

  function updateTemplateKey(id, field, value) {
    setTemplateKeys(function(prev) {
      return prev.map(function(k) {
        if (k.id !== id) return k;
        var updated = { id: k.id, name: k.name, description: k.description };
        updated[field] = value;
        return updated;
      });
    });
  }

  function removeTemplateKey(id) {
    setTemplateKeys(function(prev) { return prev.filter(function(k) { return k.id !== id; }); });
  }

  return h(
    'div',
    {
      ref: popoverRef,
      className: 'agent-editor-popover agent-editor-popover-' + (position.side || 'left'),
      style: { left: (position.left || 0) + 'px', top: (position.top || 0) + 'px' },
      onMouseDown: function(event) { event.stopPropagation(); }
    },
    h('div', { className: 'agent-editor-arrow' }),
    h('div', { className: 'agent-editor-modal' },
      h('div', { className: 'agent-editor-header' },
        h('div', { className: 'agent-editor-title' }, 'AI Settings'),
        h('button', { className: 'agent-editor-close', onClick: onClose, title: 'Close' }, '\u00d7')
      ),
      h('div', { className: 'agent-editor-subtitle' },
        'Configure AI step ',
        h('strong', null, title),
        '.'
      ),
      h('div', { className: 'agent-editor-body' },
        h('label', { className: 'agent-editor-field' },
          h('span', { className: 'agent-editor-label' }, 'System Prompt'),
          h('textarea', {
            className: 'agent-editor-textarea',
            rows: 3,
            placeholder: 'You are a helpful AI assistant.',
            value: system,
            onChange: function(e) { setSystem(e.target.value); }
          })
        ),
        h('div', { className: 'agent-editor-field' },
          h('div', { className: 'agent-editor-template-header' },
            h('span', { className: 'agent-editor-label' }, 'Available Variables'),
            h('div', { ref: varDropdownRef, style: { position: 'relative' } },
              h('button', {
                className: 'agent-editor-add-key',
                onClick: function() { setShowVarDropdown(function(v) { return !v; }); }
              }, '+ Add Variable'),
              showVarDropdown
                ? h('div', { className: 'agent-editor-var-dropdown' },
                    (function() {
                      var added = variableInputs;
                      var avail = variableNodes.filter(function(vn) {
                        var ref = '@' + String(vn.title || '').trim();
                        return String(vn.title || '').trim() && added.indexOf(ref) < 0;
                      });
                      if (avail.length === 0) {
                        return h('div', { className: 'agent-editor-var-dropdown-empty' }, 'No variables available');
                      }
                      return avail.map(function(vn) {
                        return h('div', {
                          key: vn.id,
                          className: 'agent-editor-var-dropdown-item',
                          onClick: function() { addVariableInput(vn); }
                        }, '@' + String(vn.title || ''));
                      });
                    })()
                  )
                : null
            )
          ),
          h('div', { className: 'agent-editor-var-chips' + (variableInputs.length === 0 ? ' agent-editor-var-chips-empty' : '') },
            variableInputs.length === 0
              ? h('span', { className: 'agent-editor-var-chips-placeholder' }, 'No variables added — all workflow inputs will be passed.')
              : variableInputs.map(function(ref) {
                  return h('span', { key: ref, className: 'agent-editor-var-chip' },
                    h('span', null, ref),
                    h('button', {
                      className: 'agent-editor-var-chip-remove',
                      onClick: function() { removeVariableInput(ref); },
                      title: 'Remove'
                    }, '\u00d7')
                  );
                })
          )
        ),
        h('div', { className: 'agent-editor-field' },
          h('div', { className: 'agent-editor-template-header' },
            h('span', { className: 'agent-editor-label' }, 'Response Template', h('span', { className: 'agent-editor-label-required' }, ' *')),
            h('button', {
              className: 'agent-editor-add-key',
              onClick: addTemplateKey,
              title: 'Add response field'
            }, '+ Add Field')
          ),
          h('div', { className: 'agent-editor-tree' + (templateKeys.length === 0 ? ' agent-editor-tree-empty-wrap agent-editor-tree-required' : '') },
            templateKeys.length === 0
              ? h('span', { className: 'agent-editor-tree-empty agent-editor-tree-empty-required' }, 'Required \u2014 add at least one output field.')
              : templateKeys.map(function(k, i) {
                  var isLast = i === templateKeys.length - 1;
                  return h('div', { key: k.id, className: 'agent-editor-tree-row' },
                    h('div', { className: 'agent-editor-tree-lines' },
                      h('div', { className: 'agent-editor-tree-line-v' + (isLast ? ' hidden' : '') }),
                      h('div', { className: 'agent-editor-tree-line-h' })
                    ),
                    h('span', { className: 'agent-editor-tree-dot' }),
                    h('div', { className: 'agent-editor-tree-field' },
                      h('input', {
                        className: 'agent-editor-tree-input',
                        type: 'text',
                        placeholder: 'field_name',
                        value: k.name,
                        onChange: function(e) { updateTemplateKey(k.id, 'name', e.target.value); }
                      }),
                      h('input', {
                        className: 'agent-editor-tree-desc',
                        type: 'text',
                        placeholder: 'description (optional)',
                        value: k.description,
                        onChange: function(e) { updateTemplateKey(k.id, 'description', e.target.value); }
                      })
                    ),
                    h('button', {
                      className: 'agent-editor-tree-remove',
                      onClick: function() { removeTemplateKey(k.id); },
                      title: 'Remove field'
                    }, '\u00d7')
                  );
                })
          )
        ),
        h('label', { className: 'agent-editor-field' },
          h('span', { className: 'agent-editor-label' }, 'Model Tier'),
          h('select', {
            className: 'agent-editor-input',
            value: aiTier,
            onChange: function(e) { setAiTier(e.target.value); }
          },
            h('option', { value: 'fast' }, 'Fast — lowest cost'),
            h('option', { value: 'balance' }, 'Balanced — best cost/quality'),
            h('option', { value: 'thorough' }, 'Thorough — highest quality')
          )
        ),
        h('div', { className: 'agent-editor-row' },
          h('label', { className: 'agent-editor-field' },
            h('span', { className: 'agent-editor-label' }, 'Creativity Level'),
            h('input', {
              className: 'agent-editor-input',
              type: 'number',
              min: '0',
              max: '1',
              step: '0.1',
              value: temperature,
              onChange: function(e) { setTemperature(e.target.value); }
            })
          ),
          h('label', { className: 'agent-editor-field' },
            h('span', { className: 'agent-editor-label' }, 'Response Length'),
            h('input', {
              className: 'agent-editor-input',
              type: 'number',
              min: '100',
              max: '16000',
              step: '100',
              value: maxTokens,
              onChange: function(e) { setMaxTokens(e.target.value); }
            })
          )
        )
      ),
      h('div', { className: 'agent-editor-actions' },
        h('button', { className: 'agent-editor-btn agent-editor-btn-secondary', onClick: onClose }, 'Cancel'),
        h('button', {
          className: 'agent-editor-btn agent-editor-btn-primary',
          onClick: handleSave,
          disabled: templateKeys.length === 0,
          title: templateKeys.length === 0 ? 'Add at least one response field' : undefined
        }, 'Save')
      )
    )
  );
}

module.exports = AgentSettingsEditor;
